import React, { useCallback, useEffect, useState } from 'react';
import { Menu, X, Phone, ArrowUpRight, Feather, ChevronDown } from 'lucide-react';
import { contactInfo } from '../mock';
import { LogoLockup } from './Logo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/* Items collapsed under the single "MENU" dropdown (in order). */
const menuItems = [
  { label: 'Who We Are', href: '/#vision' },
  { label: 'What We Do', href: '/#capabilities' },
  { label: 'Who We Serve', href: '/#clients' },
  { label: 'Estates Transformed', href: '/#vault' },
  { label: 'Join Our Team', href: '/#join' },
  { label: 'FAQ', href: '/#faq' },
  // 'Our Story' is an action (opens the Vault drawer) — handled inline.
];

/* Items that stay standalone in the top nav. */
const standaloneLinks = [
  { label: 'Franchise', href: '/franchise' },
  { label: 'Contact', href: '/#contact' },
];

const Header = ({ onOpenVault }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 30);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const closeMenu = () => setOpen(false);
  const openVault = () => {
    closeMenu();
    if (onOpenVault) onOpenVault();
  };

  const menuItemClass =
    'cursor-pointer font-body text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[color:var(--parchment)]/90 focus:text-[color:var(--green)] focus:bg-white/[0.04] hover:text-[color:var(--green)] transition-colors px-3 py-2';

  /* Editorial pill button used across the top nav (MENU / FRANCHISE / CONTACT).
     Sits inside the black header as a tactile, thin-bordered bubble that mirrors
     the language of the primary CTA but at a smaller, subordinate scale. */
  const pillClass =
    'inline-flex items-center gap-1.5 rounded-full border border-[color:var(--parchment)]/25 bg-white/[0.02] px-5 py-2 font-body text-[11px] font-semibold tracking-[0.16em] uppercase text-[color:var(--parchment)]/85 hover:text-[color:var(--green)] hover:border-[color:var(--green)]/60 transition-colors duration-300 outline-none';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[color:var(--obsidian)]/85 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
        <a href="#top" className="group flex items-center">
          <LogoLockup dark size="md" />
        </a>

        <nav className="hidden lg:flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="header-menu-dropdown-trigger"
              className={pillClass}
            >
              Menu <ChevronDown className="w-3.5 h-3.5 -mr-0.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={12}
              data-testid="header-menu-dropdown-content"
              className="min-w-[240px] bg-[color:var(--obsidian)]/95 backdrop-blur-xl border border-white/10 rounded-md p-1 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]"
            >
              {menuItems.map((m) => (
                <DropdownMenuItem asChild key={m.href} className={menuItemClass}>
                  <a href={m.href} data-testid={`menu-item-${m.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {m.label}
                  </a>
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); openVault(); }}
                data-testid="menu-item-our-story"
                className={menuItemClass}
              >
                <Feather className="w-3.5 h-3.5 mr-2 text-[color:var(--green)]" /> Our Story
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {standaloneLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`header-nav-${l.label.toLowerCase()}`}
              className={pillClass}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={contactInfo.phoneHref}
            data-testid="header-phone-icon"
            aria-label={`Call ${contactInfo.phone}`}
            title={contactInfo.phone}
            className="inline-grid place-items-center w-10 h-10 rounded-full border border-[color:var(--parchment)]/25 bg-white/[0.02] text-[color:var(--parchment)]/85 hover:border-[color:var(--green)]/60 hover:text-[color:var(--green)] transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
          </a>
          <a href="#sell" className="btn-primary" data-testid="header-need-our-help-btn">
            Need Our Help <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>

        <button className="lg:hidden p-2 text-[#19FD02]" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[color:var(--obsidian)] border-t border-white/5 px-6 py-6">
          <div className="flex flex-col gap-1">
            {menuItems.map((l) => (
              <a key={l.href} href={l.href} onClick={closeMenu} className="font-body font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] text-sm py-3 min-h-[44px] flex items-center hover:text-[color:var(--green)] transition-colors">
                {l.label}
              </a>
            ))}
            <button
              onClick={openVault}
              data-testid="mobile-our-story-btn"
              className="text-left font-body font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] text-sm py-3 min-h-[44px] flex items-center hover:text-[color:var(--green)] transition-colors"
            >Our Story</button>
            {standaloneLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={closeMenu} className="font-body font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] text-sm py-3 min-h-[44px] flex items-center hover:text-[color:var(--green)] transition-colors">
                {l.label}
              </a>
            ))}
            <a
              href={contactInfo.phoneHref}
              onClick={closeMenu}
              data-testid="mobile-phone-link"
              className="inline-flex items-center gap-2 font-body font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)] text-sm py-3 min-h-[44px]"
            >
              <Phone className="w-4 h-4 text-[color:var(--green)]" /> {contactInfo.phone}
            </a>
            <a href="#sell" onClick={closeMenu} className="btn-primary text-xs w-fit mt-3 min-h-[44px]">
              Need Our Help <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
